import axios, { AxiosResponse, AxiosError } from 'axios';
import env from 'utils/env';
import { getItem } from 'utils/local-storage';
import { ACCESS_TOKEN } from 'utils/storage-keys';

const config = env();
const BASE_URL = config.userServerApiUrl + '/user';

// Define a custom error type for API errors
interface ApiError {
  message: string;
  status: number;
}

// Create a function to handle API errors
const handleApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    return {
      message: error.response.data.message || 'An error occurred',
      status: error.response.status,
    };
  } else {
    return {
      message: 'An error occurred',
      status: 500,
    };
  }
};

// Create an instance of Axios with the base URL
const apiInstance = axios.create({
  baseURL: BASE_URL
});

// Define API methods
const useApiService = () => {
  const get = async <T>(url: string): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await apiInstance.get(url);
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  };

  const post = async <T, R>(url: string, data: T): Promise<R> => {
    try {
      const response: AxiosResponse<R> = await apiInstance.post(url, data, {
        headers: {
          'X-Session-Token': getItem(ACCESS_TOKEN) || ''
        }
      });
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  };

  const patch = async <T, R>(url: string, data: T): Promise<R> => {
    try {
      const response: AxiosResponse<R> = await apiInstance.patch(url, data, {
        headers: {
          'X-Session-Token': getItem(ACCESS_TOKEN) || ''
        }
      });
      return response.data;
    } catch (error: any) {
      throw handleApiError(error);
    }
  };

  return { get, post, patch };
};

export default useApiService;
